import { Card, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import type {
  AttachmentResponse,
  PropertyWithRelations,
  VisitWithRelations,
} from '@markku/types'
import { IconFileTypePdf } from '@tabler/icons-react'
import { Trans, useLingui } from '@lingui/react/macro'

interface DocumentsCardProps {
  visit: VisitWithRelations
  property?: PropertyWithRelations
}

export function DocumentsCard({ visit, property }: DocumentsCardProps) {
  const { t } = useLingui()

  const getCertificateName = (attachment: AttachmentResponse): string => {
    const certificateType = attachment.metadata?.certificateType

    if (certificateType === 'nuohoustodistus') {
      return t`Chimney sweep certificate`
    }

    return attachment.filename || t`Certificate`
  }

  const visitReport = visit.attachments?.find(
    (att) => att.metadata?.type === 'visit_report',
  )

  const certificates = (property?.attachments ?? []).filter(
    (att) =>
      att.metadata?.type === 'certificate' &&
      att.metadata?.visitId === visit.id,
  )

  if (!visitReport && certificates.length === 0) {
    return null
  }

  return (
    <Card withBorder>
      <Text size="lg" fw={600} mb="md">
        <Trans>Documents</Trans>
      </Text>

      <Stack gap="xs">
        {visitReport && (
          <DocumentLink
            href={visitReport.downloadUrl}
            label={t`Visit report`}
          />
        )}
        {certificates.map((cert) => (
          <DocumentLink
            key={cert.id}
            href={cert.downloadUrl}
            label={getCertificateName(cert)}
            description={cert.description}
          />
        ))}
      </Stack>
    </Card>
  )
}

function DocumentLink({
  href,
  label,
  description,
}: {
  href?: string | null
  label: string
  description?: string | null
}) {
  return (
    <UnstyledButton
      component="a"
      href={href || undefined}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        padding: 'var(--mantine-spacing-xs)',
        borderRadius: 'var(--mantine-radius-sm)',
        border: '1px solid var(--mantine-color-gray-3)',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-0)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent'
      }}
    >
      <Group gap="sm">
        <IconFileTypePdf size={20} />
        <Stack gap={0} style={{ flex: 1 }}>
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {description && (
            <Text size="xs" c="dimmed">
              {description}
            </Text>
          )}
        </Stack>
      </Group>
    </UnstyledButton>
  )
}
